/**
 * Paywall Renderer Module
 * UI rendering and user interactions for paywall
 */

import type {
  Paywall,
  PaywallView,
  PriceInfo,
  CurrencySymbols,
  CheckoutCloseAction,
} from '../types/paywall.types';
import type { UserRepository } from '../data/user-repository';
import type { Config } from '../types/config.types';
import type { PaddleCheckout } from './paddle-checkout';
import { isConfiguredVoucherPricingVerified } from './voucher-offer.ts';
import { getRolloutInfo } from '../core/rollout.ts';
import { BUILD_SHA } from '../core/build-info.ts';

// Currency symbols map
const CURRENCY_SYMBOLS: CurrencySymbols = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  CNY: '¥',
  SEK: 'kr',
  NZD: 'NZ$',
  MXN: '$',
  SGD: 'S$',
  HKD: 'HK$',
  NOK: 'kr',
  KRW: '₩',
  TRY: '₺',
  RUB: '₽',
  INR: '₹',
  BRL: 'R$',
  ZAR: 'R',
  DKK: 'kr',
  PLN: 'zł',
  TWD: 'NT$',
  THB: '฿',
  MYR: 'RM',
};

/**
 * Dependencies for PaywallRenderer
 */
interface PaywallRendererDeps {
  userRepository: UserRepository;
  config: Config;
  paddleCheckout?: PaddleCheckout;
  paywallConfig?: any; // PAYWALL_CONFIG from paywall-config.js
  paddlePricesMap?: any; // PADDLE_PRICES_MAP from paddle-prices.js
  congratulationsPopup?: any; // CongratulationsPopup singleton
}

/**
 * Paywall Renderer
 */
export class PaywallRenderer {
  private currentPaywall: Paywall | null = null;
  private currentView: PaywallView | null = null;
  private selectedOption: string | null = null;
  private timerInterval: number | null = null;
  private timeRemaining = 0; // Set from config in startTimer()
  private currentCheckoutType: 'checkout' | 'express-checkout' | null = null;
  private deps: PaywallRendererDeps;

  // Flag to track if paywall has been initialized
  public isInitialized = false;

  constructor() {
    this.deps = {} as PaywallRendererDeps;
  }

  /**
   * Initialize paywall UI with dependencies
   */
  init(deps: Partial<PaywallRendererDeps>): void {
    // Validate required dependencies
    if (!deps.userRepository || !deps.config) {
      throw new Error(
        'PaywallRenderer.init() requires userRepository and config dependencies'
      );
    }

    this.deps = deps as PaywallRendererDeps;
    console.log('Initializing Paywall Renderer');

    // Load paywall using the ID from userRepository (set by bootstrapper)
    if (!this.currentPaywall && this.deps.paywallConfig) {
      const paywallId = this.deps.userRepository.get('paywall');
      if (paywallId) {
        this.loadPaywall(paywallId);
      }
    }

    // Render the paywall
    this.render();

    // Set up button event listeners
    this.setupButtonListeners();

    // Mark as initialized
    this.isInitialized = true;
    console.log('Paywall Renderer initialized');

    // Start the offer countdown only if the paywall is already on screen (e.g. direct
    // #subscription access). During pre-warm the paywall is rendered "fake hidden", so the timer
    // is started by FlowManager when the user actually reaches the paywall screen.
    const paywallActive = document
      .getElementById('screen-paywall')
      ?.classList.contains('active');
    if (paywallActive) {
      this.startTimer();
    }
  }

  /**
   * Set up button event listeners
   */
  private setupButtonListeners(): void {
    const buttonIds = [
      'get-my-plan-btn',
      'get-my-plan-btn-bottom',
      'show-all-payment-methods-btn',
    ];

    for (const btnId of buttonIds) {
      const planButton = document.getElementById(btnId);
      if (planButton && this.deps.paddleCheckout) {
        planButton.addEventListener('click', () => {
          this.deps.paddleCheckout?.openCheckoutPopup();
        });
      }
    }
  }

  /**
   * Load paywall configuration
   */
  loadPaywall(paywallId: string): void {
    if (!this.deps.paywallConfig) {
      console.error('PAYWALL_CONFIG not available');
      return;
    }

    const paywall = this.deps.paywallConfig.paywalls.find(
      (p: Paywall) => p.id === paywallId
    );
    if (!paywall) {
      console.error(`Paywall ${paywallId} not found`);
      return;
    }

    this.currentPaywall = paywall;
    this.currentView = paywall.views.find(
      (v: PaywallView) => v.id === paywall.default_view
    );
    console.log('Loaded paywall:', paywall.id, 'with view:', this.currentView?.id);
  }

  /**
   * Get all price IDs from current paywall, batched by discount
   */
  getAllPriceIds(): Record<string, { priceIds: string[]; discountId: string | null }> {
    if (!this.currentPaywall) {
      return {};
    }

    // Group prices by their discount ID (or 'no_discount' if none)
    const priceBatches: Record<
      string,
      { priceIds: Set<string>; discountId: string | null }
    > = {};

    this.currentPaywall.prices.forEach((price) => {
      // Determine the batch key - either the discount ID or 'no_discount'
      const batchKey = price.discount_id || 'no_discount';

      // Initialize batch if it doesn't exist
      if (!priceBatches[batchKey]) {
        priceBatches[batchKey] = {
          priceIds: new Set(),
          discountId: price.discount_id ? this.resolvePriceId(price.discount_id) : null,
        };
      }

      // Add price IDs to the appropriate batch
      if (price.one_time_price_id) {
        const resolved = this.resolvePriceId(price.one_time_price_id);
        if (resolved && !resolved.includes('replace')) {
          priceBatches[batchKey].priceIds.add(resolved);
        }
      }
      if (price.recurring_price_id) {
        const resolved = this.resolvePriceId(price.recurring_price_id);
        if (resolved && !resolved.includes('replace')) {
          priceBatches[batchKey].priceIds.add(resolved);
        }
      }
    });

    // Convert Sets to Arrays for the final structure
    const result: Record<string, { priceIds: string[]; discountId: string | null }> = {};
    for (const [key, batch] of Object.entries(priceBatches)) {
      if (batch.priceIds.size > 0) {
        result[key] = {
          priceIds: Array.from(batch.priceIds),
          discountId: batch.discountId,
        };
      }
    }

    return result;
  }

  /**
   * Resolve price ID from paddle-prices.js
   */
  private resolvePriceId(key: string): string {
    if (!this.deps.paddlePricesMap) {
      return key;
    }

    const env = this.deps.config.environment === 'sandbox' ? 'sandbox' : 'production';
    const priceEntry = this.deps.paddlePricesMap.environment[env][key];

    if (typeof priceEntry === 'object' && priceEntry !== null) {
      return priceEntry.paddle_id;
    }
    return priceEntry || key;
  }

  /**
   * Get price info from paddle-prices.js
   */
  getPriceInfo(key: string): PriceInfo {
    if (!this.deps.paddlePricesMap) {
      return {
        paddleId: key,
        price: null,
        period: null,
        trial: null,
        type: 'price',
      };
    }

    const env = this.deps.config.environment === 'sandbox' ? 'sandbox' : 'production';
    const priceEntry = this.deps.paddlePricesMap.environment[env][key];

    if (typeof priceEntry === 'object' && priceEntry !== null) {
      return {
        paddleId: priceEntry.paddle_id,
        price: priceEntry.price,
        period: priceEntry.period,
        trial: priceEntry.trial,
        type: priceEntry.type || 'price',
        percentage: priceEntry.percentage,
      };
    }

    return {
      paddleId: priceEntry || key,
      price: null,
      period: null,
      trial: null,
      type: 'price',
    };
  }

  /**
   * Render the paywall UI
   */
  render(): void {
    console.log('Rendering paywall');

    if (!this.currentView) {
      console.error('No view to render');
      return;
    }

    // Update voucher
    this.updateVoucher();

    // Update price options
    this.updatePriceOptions();

    // Update disclaimer
    this.updateDisclaimer();

    // Update action buttons
    this.updateActionButtons();

    // Handle inline checkout mode
    const checkoutItem = this.currentView.below?.items?.find(
      (item) => item.type === 'checkout' || item.type === 'express-checkout'
    );
    if (checkoutItem) {
      // Show inline checkout container
      const inlineContainer = document.getElementById('paddle-inline-checkout');
      if (inlineContainer) {
        inlineContainer.style.display = 'block';
        // Store the checkout type for later use
        this.currentCheckoutType = checkoutItem.type as 'checkout' | 'express-checkout';
        // Open inline checkout immediately
        this.openInlineCheckout();
      }
    }
  }

  /**
   * Update voucher display
   */
  private updateVoucher(): void {
    const voucherElement = document.getElementById('paywall-voucher');
    const discountText = document.getElementById('voucher-discount-text');
    const codeText = document.getElementById('voucher-code-text');
    const discountBadges = document.querySelectorAll<HTMLElement>('.discount-badge');
    const modernDiscountBadges = document.querySelectorAll<HTMLElement>(
      '[data-v2-paywall-discount-badge]'
    );

    if (!voucherElement) return;

    const isModern = String(this.deps.userRepository.getABTestVariant()) === 'modern';
    const pricingVerified = !isModern || this.isCurrentVoucherPricingVerified();

    if (this.currentView?.voucher && pricingVerified) {
      const voucherCode = this.generateVoucherCode(this.currentView.voucher.code);
      const discountPercent = this.currentView.voucher.discount;

      // Update main voucher
      if (discountText) discountText.textContent = `${discountPercent}% OFF`;
      if (codeText) codeText.textContent = voucherCode;
      voucherElement.style.display = 'block';

      // Update discount badge (mini voucher) to match
      discountBadges.forEach((discountBadge) => {
        discountBadge.textContent = `${discountPercent}% OFF`;
        discountBadge.style.removeProperty('display');
      });
      modernDiscountBadges.forEach((discountBadge) => {
        discountBadge.style.removeProperty('display');
      });
    } else {
      voucherElement.style.display = 'none';
      // Hide discount claims if Paddle did not verify every Modern amount due now.
      discountBadges.forEach((discountBadge) => {
        discountBadge.style.display = 'none';
      });
      modernDiscountBadges.forEach((discountBadge) => {
        discountBadge.style.display = 'none';
      });
    }
  }

  private isCurrentVoucherPricingVerified(): boolean {
    if (
      !this.currentPaywall
      || !this.currentView
      || !this.deps.paywallConfig
      || !this.deps.paddleCheckout
    ) {
      return false;
    }

    return isConfiguredVoucherPricingVerified(
      this.deps.paywallConfig,
      this.currentPaywall.id,
      {
        resolvePriceId: (key) => this.resolvePriceId(key),
        getPriceData: (priceId, discountId) => (
          this.deps.paddleCheckout?.getPriceData(priceId, discountId) || null
        ),
      },
      this.currentView.id
    );
  }

  /**
   * Generate voucher code
   */
  private generateVoucherCode(template: string): string {
    const now = new Date();
    const monthNames = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ];

    const month = monthNames[now.getMonth()];
    const year = String(now.getFullYear());
    return template
      .replace('${month_3_letter_uppercase}', month || 'JAN')
      .replace('${year}', year);
  }

  /**
   * Update disclaimer text
   */
  private updateDisclaimer(): void {
    const disclaimerElement = document.querySelector('.disclaimer') as HTMLElement;
    if (!disclaimerElement || !this.selectedOption) return;

    const selectedOption = this.currentView?.options.find(
      (o) => o.price === this.selectedOption
    );
    if (!selectedOption) return;

    const pricesData = (this.deps.paddleCheckout as any)?.pricesData || {};
    const firstPriceKey = Object.keys(pricesData)[0];
    const currency = firstPriceKey ? pricesData[firstPriceKey]?.currency || 'USD' : 'USD';
    const currencySymbol = this.getCurrencySymbol(currency);

    // Calculate first payment and standard price (these are subtotals - pre-tax)
    const payNow = this.calculatePrice(selectedOption.pay_now, selectedOption.price);
    const payLater = this.calculatePrice(selectedOption.pay_later, selectedOption.price);
    const recurringPeriod = selectedOption.pay_later_recurring_period || 'monthly';

    // Get tax info
    const taxInfo = this.getTaxInfo(selectedOption.price);
    const taxSuffix = taxInfo.isTaxExclusive ? '+tax' : '';

    // Format the values
    const firstPayment = `${currencySymbol}${payNow.toFixed(2)}${taxSuffix}`;
    const standardPriceWithPeriod = `${currencySymbol}${payLater.toFixed(2)}${taxSuffix} per ${
      recurringPeriod === 'annual'
        ? 'year'
        : recurringPeriod === 'every 3 months'
        ? '3 months'
        : recurringPeriod === 'every 7 days'
        ? 'week'
        : 'month'
    }`;

    // Build the disclaimer HTML with links
    const disclaimerHTML = `<p>This is an auto-renewing subscription designed to give you uninterrupted access to all Premium features.</p>
            <p>Your first payment of ${firstPayment} grants you full Premium access at a special introductory rate. Once this initial period ends, your subscription will automatically renew at the standard price of ${standardPriceWithPeriod}. Payments will continue on a recurring ${
      recurringPeriod === 'annual'
        ? 'annual'
        : recurringPeriod === 'every 3 months'
        ? '3-month'
        : recurringPeriod === 'every 7 days'
        ? 'weekly'
        : recurringPeriod === 'every 4 weeks'
        ? '4-week'
        : recurringPeriod === 'every 12 weeks'
        ? '12-week'
        : 'monthly'
    } basis unless you cancel your subscription before the next billing date.</p>
            <p>You can manage or cancel your subscription at any time through your account settings. Please note that by subscribing, you acknowledge and agree to our <a href="policy/" target="_blank" style="text-decoration: underline;">Privacy Policy</a>, <a href="terms/" target="_blank" style="text-decoration: underline;">Terms of Use</a>, and <a href="refund/" target="_blank" style="text-decoration: underline;">Refund Policy</a>, which outline how we handle your data, payments, and user rights.</p>`;

    disclaimerElement.innerHTML = disclaimerHTML;
  }

  /**
   * Update price options
   */
  private updatePriceOptions(): void {
    // Get both containers
    const containers = [
      document.getElementById('paywall-options'),
      document.getElementById('paywall-options-2'),
    ];

    // Check if at least one container exists and we have a view
    if (!this.currentView) return;
    const hasValidContainer = containers.some((c) => c !== null);
    if (!hasValidContainer) return;

    const pricesData = (this.deps.paddleCheckout as any)?.pricesData || {};
    const firstPriceKey = Object.keys(pricesData)[0];
    const currency = firstPriceKey ? pricesData[firstPriceKey]?.currency || 'USD' : 'USD';
    const currencySymbol = this.getCurrencySymbol(currency);

    // Process each container
    containers.forEach((container, containerIndex) => {
      if (!container) return;

      // Clear existing options
      container.innerHTML = '';

      // Add each option
      this.currentView!.options.forEach((option, index) => {
        const priceData = this.currentPaywall?.prices.find((p) => p.id === option.price);
        if (!priceData) return;

        // Calculate prices
        const crossedPrice = this.calculatePrice(option.crossed_price, option.price);
        const uncrossedPrice = this.calculatePrice(option.uncrossed_price, option.price);
        const dailyPrice = this.calculatePrice(option.daily_price, option.price);
        const dailyCrossedPrice = this.calculatePrice(option.daily_crossed_price, option.price);

        // Get trial info
        let trialInfo = '';
        const priceInfo = priceData.recurring_price_id
          ? this.getPriceInfo(priceData.recurring_price_id)
          : priceData.one_time_price_id
          ? this.getPriceInfo(priceData.one_time_price_id)
          : null;
        if (priceInfo?.trial && !priceData.one_time_price_id) {
          trialInfo = `<span class="trial-badge">${priceInfo.trial} free</span>`;
        }

        // Get the price key for this option
        const priceKey = option.price;

        // Check if this should be selected by default (only on first container)
        const isSelected = option.badge
          ? true
          : index === 0 && !this.currentView!.options.find((o) => o.badge);
        if (isSelected && !this.selectedOption && containerIndex === 0) {
          this.selectedOption = priceKey;
        }

        // Create option element
        const optionDiv = document.createElement('div');
        optionDiv.className = `price-option ${
          this.selectedOption === priceKey ? 'selected' : ''
        }`;
        optionDiv.setAttribute('data-price-key', priceKey);

        // Make the entire option clickable
        optionDiv.onclick = (e) => {
          // Prevent double-firing if clicking the radio button directly
          if ((e.target as HTMLElement).getAttribute('type') !== 'radio') {
            this.selectOption(priceKey);
          }
        };

        // Use unique radio group name for each container
        const radioGroupName = containerIndex === 0 ? 'price-option' : 'price-option-2';

        // Get tax info for this option
        const taxInfo = this.getTaxInfo(priceKey);
        const taxBadge = taxInfo.isTaxExclusive ? '<span class="tax-badge">+ tax</span>' : '';

        optionDiv.innerHTML = `
                ${option.badge ? `<div class="option-badge">${option.badge}</div>` : ''}
                <div class="option-content">
                    <div class="option-left">
                        <input type="radio"
                               name="${radioGroupName}"
                               value="${priceKey}"
                               ${this.selectedOption === priceKey ? 'checked' : ''}>
                        <div class="option-details">
                            <div class="option-title">${option.title} ${trialInfo}</div>
                            <div class="option-subtitle">${option.subtitle || ''}</div>
                            <div class="option-price">
                                <span class="crossed-price">${currencySymbol}${crossedPrice.toFixed(2)}</span>
                                →
                                <span class="uncrossed-price">${currencySymbol}${uncrossedPrice.toFixed(2)}</span>
                                ${taxBadge}
                            </div>
                        </div>
                    </div>
                    <div class="option-right">
                        <svg class="pricing-option-right-bg" viewBox="0 0 100 60" preserveAspectRatio="none">
                          <defs>
                            <filter id="pricing-inset-shadow" x="-50%" y="-50%" width="200%" height="200%">
                              <feComponentTransfer in="SourceAlpha">
                                <feFuncA type="table" tableValues="1 0"></feFuncA>
                              </feComponentTransfer>
                              <feGaussianBlur stdDeviation="2"></feGaussianBlur>
                              <feOffset dx="0" dy="1" result="shadow"></feOffset>
                              <feFlood flood-color="rgba(0,0,0,0.15)" result="color"></feFlood>
                              <feComposite in="color" in2="shadow" operator="in" result="shadow"></feComposite>
                              <feComposite in="shadow" in2="SourceAlpha" operator="in"></feComposite>
                              <feMerge>
                                <feMergeNode in="SourceGraphic"></feMergeNode>
                                <feMergeNode></feMergeNode>
                              </feMerge>
                            </filter>
                          </defs>
                          <path d="M30 1 H89 Q99 1 99 11 V49 Q99 59 89 59 H30 Q22 59 7 37 Q1 30 7 23 Q22 1 30 1 Z"></path>
                          <!-- <path d="M30 1 H89 Q99 1 99 11 V49 Q99 59 89 59 H30 Q22 59 7 37 Q1 30 7 23 Q22 1 30 1 Z" filter="url(#pricing-inset-shadow)"></path> -->
                        </svg>
                        <div class="daily-crossed-amount">${currencySymbol}${dailyCrossedPrice.toFixed(2)}</div>
                        <div class="daily-amount">${currencySymbol}${dailyPrice.toFixed(2)}</div>
                        <div class="daily-label">per day</div>
                    </div>
                </div>
            `;

        // Add change event listener to radio button after adding to DOM
        container.appendChild(optionDiv);
        const radio = optionDiv.querySelector('input[type="radio"]') as HTMLInputElement;
        if (radio) {
          radio.onchange = () => this.selectOption(priceKey);
        }
      });
    });
  }

  /**
   * Update action buttons
   */
  private updateActionButtons(): void {
    const container = document.getElementById('paywall-action-buttons');
    if (!container || !this.currentView?.below?.items) return;

    // Clear existing buttons
    container.innerHTML = '';

    this.currentView.below.items.forEach((item) => {
      if (item.type === 'button' && 'text' in item && 'action' in item) {
        const button = document.createElement('button');
        button.className = 'paywall-button action-button primary';
        button.textContent = item.text;
        button.onclick = () => this.handleAction(item.action);
        container.appendChild(button);
      }
    });
  }

  /**
   * Calculate price from formula
   * - Tax exclusive regions (NY): uses subtotal (pre-tax) - customer sees price + tax added
   * - Tax inclusive regions (Cyprus): uses total - customer sees final price with tax included
   */
  private calculatePrice(formula: string, priceKey: string): number {
    if (!formula || !priceKey) return 0;

    const price = this.currentPaywall?.prices.find((p) => p.id === priceKey);
    if (!price) {
      console.log('Price configuration not found for:', priceKey);
      return 0;
    }

    // Get base price IDs
    const oneTimeId = this.resolvePriceId(price.one_time_price_id || '');
    const recurringId = this.resolvePriceId(price.recurring_price_id || '');
    const discountId = price.discount_id ? this.resolvePriceId(price.discount_id) : null;

    // Get price data using the helper that handles discount-specific lookups
    const oneTimePriceData = (this.deps.paddleCheckout as any)?.getPriceData?.(
      oneTimeId,
      discountId
    );
    const recurringPriceData = (this.deps.paddleCheckout as any)?.getPriceData?.(
      recurringId,
      discountId
    );

    // Determine which price to use based on tax mode:
    // - Tax exclusive: use subtotal (tax will be added at checkout)
    // - Tax inclusive: use total (price already includes tax)
    const oneTimeIsTaxExclusive = oneTimePriceData?.isTaxExclusive ?? false;
    const recurringIsTaxExclusive = recurringPriceData?.isTaxExclusive ?? false;

    // For one-time prices
    const oneTimePrice = oneTimeIsTaxExclusive
      ? (oneTimePriceData?.subtotalCents || 0)
      : (oneTimePriceData?.cents || 0);
    const oneTimeOriginalPrice = oneTimeIsTaxExclusive
      ? (oneTimePriceData?.originalSubtotalCents || oneTimePrice)
      : (oneTimePriceData?.originalCents || oneTimePrice);
    const oneTimeDiscountedPrice = oneTimePrice; // Current price is already discounted if applicable

    // For recurring prices
    const recurringPrice = recurringIsTaxExclusive
      ? (recurringPriceData?.subtotalCents || 0)
      : (recurringPriceData?.cents || 0);
    const recurringOriginalPrice = recurringIsTaxExclusive
      ? (recurringPriceData?.originalSubtotalCents || recurringPrice)
      : (recurringPriceData?.originalCents || recurringPrice);
    const recurringDiscountedPrice = recurringPrice; // Current price is already discounted if applicable

    // Replace placeholders in formula
    let calculation = formula;
    calculation = calculation.replace(/\$\{one_time_price_id\}/g, String(oneTimePrice));
    calculation = calculation.replace(/\$\{one_time_price_id_original\}/g, String(oneTimeOriginalPrice));
    calculation = calculation.replace(/\$\{one_time_price_id_discounted\}/g, String(oneTimeDiscountedPrice));
    calculation = calculation.replace(/\$\{recurring_price_id\}/g, String(recurringPrice));
    calculation = calculation.replace(/\$\{recurring_price_id_original\}/g, String(recurringOriginalPrice));
    calculation = calculation.replace(/\$\{recurring_price_id_discounted\}/g, String(recurringDiscountedPrice));

    try {
      // Validate that calculation only contains safe characters (numbers, operators, parentheses, whitespace)
      if (!/^[\d\s+\-*/().]+$/.test(calculation)) {
        console.error('Invalid characters in price formula:', formula);
        return 0;
      }
      // Use Function constructor instead of eval (safer - no access to local scope)
      const result = new Function('return ' + calculation)();
      return result / 100; // Convert to dollars
    } catch (error) {
      console.error('Error calculating price:', error, formula);
      return 0;
    }
  }

  /**
   * Get tax info for a price key
   */
  private getTaxInfo(priceKey: string): { isTaxExclusive: boolean; taxCents: number; taxDollars: number } {
    const price = this.currentPaywall?.prices.find((p) => p.id === priceKey);
    if (!price) {
      return { isTaxExclusive: false, taxCents: 0, taxDollars: 0 };
    }

    const recurringId = this.resolvePriceId(price.recurring_price_id || '');
    const oneTimeId = this.resolvePriceId(price.one_time_price_id || '');
    const discountId = price.discount_id ? this.resolvePriceId(price.discount_id) : null;

    // Get the primary price data (prefer recurring, fallback to one-time)
    const priceData = (this.deps.paddleCheckout as any)?.getPriceData?.(
      recurringId || oneTimeId,
      discountId
    );

    if (!priceData) {
      return { isTaxExclusive: false, taxCents: 0, taxDollars: 0 };
    }

    return {
      isTaxExclusive: priceData.isTaxExclusive || false,
      taxCents: priceData.taxCents || 0,
      taxDollars: priceData.taxDollars || 0
    };
  }

  /**
   * Select price option
   */
  selectOption(priceKey: string): void {
    console.log('Selected option:', priceKey);
    this.selectedOption = priceKey;

    // Update UI - remove selected class from all, then add to the selected one
    document.querySelectorAll('.price-option').forEach((el) => {
      el.classList.remove('selected');
    });

    // Update all elements with matching price key to add selected class
    document.querySelectorAll(`.price-option[data-price-key="${priceKey}"]`).forEach((el) => {
      el.classList.add('selected');
    });

    // Update radio buttons in all containers
    document
      .querySelectorAll<HTMLInputElement>(`input[type="radio"][value="${priceKey}"]`)
      .forEach((radio) => {
        if (!radio.checked) {
          radio.checked = true;
        }
      });

    // Update disclaimer with new payment terms
    this.updateDisclaimer();

    // Check if inline checkout is visible and update it
    const inlineContainer = document.getElementById('paddle-inline-checkout');
    const isInlineCheckoutVisible =
      inlineContainer && inlineContainer.style.display !== 'none';

    // Check if popup checkout is visible
    const popupOverlay = document.getElementById('checkout-popup-overlay');
    const isPopupCheckoutVisible = popupOverlay && popupOverlay.style.display !== 'none';

    // If any checkout is open, update it with the new price
    if (isInlineCheckoutVisible || isPopupCheckoutVisible) {
      const frameTarget = isInlineCheckoutVisible
        ? 'paddle-checkout'
        : 'popup-paddle-checkout';

      this.tryUpdateCheckout().then((updateSuccessful) => {
        if (!updateSuccessful) {
          console.log('Update failed, falling back to reload checkout');

          // Close existing checkout
          this.deps.paddleCheckout?.closeCheckout();
          // Reopen with new price
          setTimeout(() => {
            this.openPaddleCheckout(frameTarget, false);
          }, 100);
        } else {
          console.log('Checkout updated successfully without reload');
        }
      });
    }
  }

  /**
   * Try to update checkout without reload
   */
  private async tryUpdateCheckout(): Promise<boolean> {
    const selectedPrice = this.currentPaywall?.prices.find(
      (p) => p.id === this.selectedOption
    );
    if (!selectedPrice) {
      console.error('Selected price not found for update');
      return false;
    }

    // Build items array - include both one-time and recurring if both present
    const items: Array<{ priceId: string }> = [];

    if (selectedPrice.one_time_price_id) {
      const oneTimePriceId = this.resolvePriceId(selectedPrice.one_time_price_id);
      if (oneTimePriceId) {
        items.push({ priceId: oneTimePriceId });
      }
    }

    if (selectedPrice.recurring_price_id) {
      const recurringPriceId = this.resolvePriceId(selectedPrice.recurring_price_id);
      if (recurringPriceId) {
        items.push({ priceId: recurringPriceId });
      }
    }

    if (items.length === 0) {
      console.error('No price IDs found for update');
      return false;
    }

    const discountId = selectedPrice.discount_id
      ? this.resolvePriceId(selectedPrice.discount_id)
      : null;

    // Build update config with items array
    const updateConfig: any = {
      items: items,
    };

    if (discountId) {
      updateConfig.discountId = discountId;
    }
    return (
      (await this.deps.paddleCheckout?.updateCheckout(updateConfig)) || false
    );
  }

  /**
   * Handle button actions
   */
  private handleAction(action: string): void {
    switch (action) {
      case 'open-checkout-popup':
        this.openCheckoutPopup();
        break;
      case 'show-extra-discount':
        this.switchView('extra_discount');
        break;
      default:
        console.log('Unknown action:', action);
    }
  }

  /**
   * Switch paywall view
   */
  switchView(viewId: string): void {
    const newView = this.currentPaywall?.views.find((v) => v.id === viewId);
    if (!newView) {
      console.error('View not found:', viewId);
      return;
    }

    console.log('Switching to view:', viewId);
    this.currentView = newView;
    this.selectedOption = null; // Reset selection
    this.render();
  }

  /**
   * Open checkout in popup mode
   */
  openCheckoutPopup(): void {
    if (!this.selectedOption) {
      console.error('No option selected');
      return;
    }

    // Show popup overlay
    const overlay = document.getElementById('checkout-popup-overlay');
    if (overlay) {
      overlay.style.display = 'flex';
      overlay.removeAttribute('aria-hidden');
    }

    // Open Paddle checkout in popup
    this.openPaddleCheckout('popup-paddle-checkout', false);
  }

  /**
   * Open inline checkout
   */
  openInlineCheckout(): void {
    if (!this.selectedOption) {
      // Auto-select first option or badged option
      const badgedOption = this.currentView?.options.find((o) => o.badge);
      this.selectedOption = badgedOption
        ? badgedOption.price
        : this.currentView?.options[0]?.price || null;
      if (this.selectedOption) {
        this.updatePriceOptions(); // Update UI to show selection
      }
    }

    if (!this.selectedOption) {
      console.error('No option available for checkout');
      return;
    }

    // Open Paddle checkout inline
    this.openPaddleCheckout('paddle-checkout', false);
  }

  /**
   * Open Paddle checkout (delegates to PaddleCheckout)
   */
  private openPaddleCheckout(frameTarget: string, overlay: boolean): void {
    const selectedPrice = this.currentPaywall?.prices.find(
      (p) => p.id === this.selectedOption
    );
    if (!selectedPrice) {
      console.error('Selected price not found');
      return;
    }

    // Build items array - include both one-time and recurring if both present
    const items: Array<{ priceId: string }> = [];

    // Add one-time price first (charged today)
    if (selectedPrice.one_time_price_id) {
      const oneTimePriceId = this.resolvePriceId(selectedPrice.one_time_price_id);
      if (oneTimePriceId) {
        items.push({ priceId: oneTimePriceId });
      }
    }

    // Add recurring price (with trial if configured in Paddle)
    if (selectedPrice.recurring_price_id) {
      const recurringPriceId = this.resolvePriceId(selectedPrice.recurring_price_id);
      if (recurringPriceId) {
        items.push({ priceId: recurringPriceId });
      }
    }

    if (items.length === 0) {
      console.error('No price IDs found for selected option');
      return;
    }

    console.log('Building checkout with items:', items);

    const discountId = selectedPrice.discount_id
      ? this.resolvePriceId(selectedPrice.discount_id)
      : null;

    // Get user data from UserRepository
    const userData = this.deps.userRepository.getAllData();

    // Update checkout total amount display
    const selectedOption = this.currentView?.options.find(
      (o) => o.price === this.selectedOption
    );
    const firstItem = items[0];
    if (selectedOption && firstItem) {
      const firstPriceId = firstItem.priceId;
      // Use getPriceData to get the discounted price with correct tax calculation
      const priceData = (this.deps.paddleCheckout as any)?.getPriceData?.(firstPriceId, discountId);
      if (priceData) {
        const totalAmountElements = document.querySelectorAll<HTMLElement>(
          '.checkout-total-amount'
        );
        if (totalAmountElements.length > 0) {
          const currency = priceData.currency || 'USD';
          const currencySymbol = this.getCurrencySymbol(currency);
          // Use pay_now from config (subtotal - pre-tax amount)
          const payNow = this.calculatePrice(selectedOption.pay_now, selectedOption.price);
          const formattedAmount = `${currencySymbol}${payNow.toFixed(2)}`;
          const planTitle = selectedOption.title || 'Plan';

          // Get tax info directly from the first item's price data (one-time price if present)
          // This ensures we show the tax for what's being charged today, not the recurring price
          const isTaxExclusive = priceData.isTaxExclusive || false;
          const taxDollars = priceData.taxDollars || 0;
          let totalText: string;

          if (isTaxExclusive && taxDollars > 0) {
            // Show "$3.99 (+$0.35 tax) for 1-Week Trial"
            const formattedTax = `${currencySymbol}${taxDollars.toFixed(2)}`;
            totalText = `${formattedAmount} (+${formattedTax} tax) for ${planTitle}`;
          } else {
            // Tax inclusive - just show the amount
            totalText = `${formattedAmount} for ${planTitle}`;
          }

          totalAmountElements.forEach((element) => {
            element.textContent = totalText;
          });
        }
      }
    }

    // Build checkout configuration with items array
    // Rollout arm + build sha ride along so revenue can be reconciled per release arm straight
    // from Paddle exports (which build actually took the money), without joining via Amplitude.
    const rollout = getRolloutInfo();
    const config: any = {
      items: items,
      email: userData.email,
      frameTarget: frameTarget,
      displayMode: overlay ? 'overlay' : 'inline',
      customData: {
        email: userData.email || '',
        userId: userData.email || 'guest',
        selectedPlan: this.selectedOption,
        ...this.deps.userRepository.getCampaignParams(),
        ab_test_variant: this.deps.userRepository.getABTestVariant(),
        ...(rollout ? { rollout_arm: rollout.arm, rollout_epoch: String(rollout.epoch) } : {}),
        build_sha: BUILD_SHA,
      },
    };

    // Modern collects a longer questionnaire; keep those answers out of Paddle billing metadata.
    const isModern = String(this.deps.userRepository.getABTestVariant()) === 'modern';
    if (!isModern && userData.answers) {
      for (const question in userData.answers) {
        config.customData['answer_' + question] = userData.answers[question];
      }
    }

    if (discountId) {
      config.discountId = discountId;
    }

    // Check if this is an inline checkout that should use express mode
    const isInlineCheckout = frameTarget === 'paddle-checkout';
    const useExpressMode = isInlineCheckout && this.currentCheckoutType === 'express-checkout';

    // Open checkout via PaddleCheckout
    this.deps.paddleCheckout?.openCheckout(config, useExpressMode);
  }

  /**
   * Close checkout popup
   */
  closeCheckoutPopup(): void {
    // Hide popup overlay
    const overlay = document.getElementById('checkout-popup-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }

    // Close Paddle checkout
    this.deps.paddleCheckout?.closeCheckout();

    // Update the inline checkout with currently selected option
    if (this.selectedOption) {
      console.log('Updating inline checkout after popup close');
      this.openInlineCheckout();
    }

    // Handle on_checkout_popup_close actions
    if (this.currentView?.on_checkout_popup_close) {
      const actions = Array.isArray(this.currentView.on_checkout_popup_close)
        ? this.currentView.on_checkout_popup_close
        : [this.currentView.on_checkout_popup_close];

      // Process each action
      actions.forEach((actionConfig: CheckoutCloseAction) => {
        const { action, target_view, discount_percent } = actionConfig;

        if (action === 'show_discount_congratulations' && discount_percent) {
          // Show congratulations popup
          if (this.deps.congratulationsPopup) {
            this.deps.congratulationsPopup.show(discount_percent, () => {
              // Continue with next actions after popup closes
              console.log('Congratulations popup closed');
            });
          }
        } else if (action === 'switch_view' && target_view) {
          // Switch to the target view
          // Add a small delay if congratulations popup was shown
          const delay = actions.some(
            (a: CheckoutCloseAction) => a.action === 'show_discount_congratulations'
          )
            ? 300
            : 0;
          setTimeout(() => {
            this.switchView(target_view);
          }, delay);
        } else {
          console.log('Unknown on_checkout_popup_close action:', action);
        }
      });
    }
  }

  /**
   * Update prices from Paddle event
   */
  updatePricesFromEvent(data: any): void {
    if (data.items && data.currencyCode) {
      console.log(`Updating prices for currency: ${data.currencyCode}`);
      // Re-render with new currency
      this.render();
    }
  }

  /**
   * Start countdown timer
   */
  startTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    // Always start fresh from config duration (pure decoration)
    this.timeRemaining = this.deps.config.timer.offerDuration * 60;

    // Update display immediately
    this.updateTimerDisplay();

    // Update every second
    this.timerInterval = window.setInterval(() => {
      this.timeRemaining--;
      if (this.timeRemaining <= 0) {
        this.timeRemaining = 0;
        clearInterval(this.timerInterval!);
        this.handleTimerExpired();
      }
      this.updateTimerDisplay();
    }, 1000);
  }

  /**
   * Handle timer expiration
   */
  private handleTimerExpired(): void {
    // Hide the entire paywall timer section
    const paywallTimer = document.querySelector('.paywall-timer') as HTMLElement;
    if (paywallTimer) {
      paywallTimer.style.display = 'none';
    }

    // Hide the voucher timer row (second row in voucher section)
    const voucherCountdown = document.getElementById('voucher-countdown');
    if (voucherCountdown) {
      const voucherTimerRow = voucherCountdown.closest('.voucher-row') as HTMLElement;
      if (voucherTimerRow) {
        voucherTimerRow.style.display = 'none';
      }
    }
  }

  /**
   * Update timer display
   */
  private updateTimerDisplay(): void {
    const element = document.getElementById('paywall-countdown');
    const voucherTimer = document.getElementById('voucher-countdown');

    const minutes = Math.floor(this.timeRemaining / 60);
    const seconds = this.timeRemaining % 60;
    const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    if (element) {
      element.textContent = timeString;
    }

    if (voucherTimer) {
      voucherTimer.textContent = timeString;
    }
  }

  /**
   * Get currency symbol
   */
  private getCurrencySymbol(currency: string): string {
    return CURRENCY_SYMBOLS[currency] || currency + ' ';
  }
}

// Create singleton instance
export const paywallRenderer = new PaywallRenderer();
